import { memo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BarChart3, Share2 } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import type { Script, ScriptForm } from "@/features/dashboard/types";

interface FormScoreCellProps {
  script: Script;
  scriptForm: ScriptForm;
  score: number | null;
  onShare: (script: Script, scriptForm: ScriptForm) => void;
  onAnalyze: (scriptForm: ScriptForm, scriptName: string, script: Script) => void;
  language: string;
}

const formatScore = (score: number | null) => {
  if (typeof score !== "number" || Number.isNaN(score)) {
    return "—";
  }
  return score.toFixed(1);
};

export const FormScoreCell = memo(({
  script,
  scriptForm,
  score,
  onShare,
  onAnalyze,
  language,
}: FormScoreCellProps) => {
  const { t } = useTranslation();
  const scoreValue = formatScore(score);
  const hasScore = typeof score === "number" && !Number.isNaN(score);
  const submissionCount = scriptForm.feedback_submissions?.length ?? 0;

  const formName = language === "th" && scriptForm.feedback_forms.name_th
    ? scriptForm.feedback_forms.name_th
    : scriptForm.feedback_forms.name;

  const getScoreCellStyles = (s: number | null) => {
    if (typeof s !== "number" || Number.isNaN(s)) {
      return cn(
        "border-dashed border-slate-300 bg-slate-50/70 text-slate-600",
        "hover:border-primary hover:bg-primary/5 hover:text-primary",
        "dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400 dark:hover:border-primary dark:hover:bg-primary/10"
      );
    }
    if (s >= 4.0) {
      return cn(
        "border-emerald-200/90 bg-emerald-50/90 text-emerald-900 shadow-2xs",
        "hover:border-emerald-400 hover:bg-emerald-100/90 hover:shadow-xs",
        "dark:border-emerald-800/70 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
      );
    }
    if (s >= 3.0) {
      return cn(
        "border-amber-200/90 bg-amber-50/90 text-amber-900 shadow-2xs",
        "hover:border-amber-400 hover:bg-amber-100/90 hover:shadow-xs",
        "dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-900/50"
      );
    }
    return cn(
      "border-rose-200/90 bg-rose-50/90 text-rose-900 shadow-2xs",
      "hover:border-rose-400 hover:bg-rose-100/90 hover:shadow-xs",
      "dark:border-rose-800/70 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-900/50"
    );
  };

  const reviewCountText = language === "th"
    ? `${submissionCount} รีวิว`
    : submissionCount === 1
      ? "1 review"
      : `${submissionCount} reviews`;

  return (
    <div className="group relative flex h-full w-full items-center justify-center">
      <button
        type="button"
        className={cn(
          "relative flex h-16 w-full flex-col items-center justify-center rounded-xl border px-3 py-1.5 transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
          getScoreCellStyles(score)
        )}
        aria-label={
          hasScore
            ? t("dashboard.table.score_aria", {
                script: script.movie_name,
                form: formName,
                score: scoreValue,
              })
            : t("dashboard.table.no_reviews_aria", {
                script: script.movie_name,
                form: formName,
              })
        }
        onClick={() => {
          if (hasScore) {
            onAnalyze(scriptForm, script.movie_name, script);
          } else {
            onShare(script, scriptForm);
          }
        }}
      >
        {hasScore ? (
          <>
            <div className="flex items-baseline gap-1">
              <span className="tabular-nums text-xl font-bold tracking-tight">{scoreValue}</span>
              <span className="text-xs opacity-60">/ 5</span>
            </div>
            <span className="text-[11px] font-medium opacity-75">{reviewCountText}</span>
          </>
        ) : (
          <>
            <span className="text-xs font-semibold">{t("dashboard.table.no_reviews")}</span>
            <span className="text-[11px] opacity-75">{t("dashboard.table.share_with_team")}</span>
          </>
        )}
      </button>

      {/* Quick Action Overlay Buttons */}
      <div className="pointer-events-none absolute right-1.5 top-1.5 flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
        {hasScore && (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            className="h-6 w-6 rounded-md border border-slate-200/90 bg-white/95 text-slate-700 shadow-2xs hover:border-primary hover:bg-white hover:text-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title={t("dashboard.table.open_analytics")}
            onClick={(e) => {
              e.stopPropagation();
              onAnalyze(scriptForm, script.movie_name, script);
            }}
          >
            <BarChart3 className="h-3 w-3" />
            <span className="sr-only">{t("dashboard.table.open_analytics")}</span>
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          type="button"
          className="h-6 w-6 rounded-md border border-slate-200/90 bg-white/95 text-slate-700 shadow-2xs hover:border-primary hover:bg-white hover:text-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          title={t("dashboard.table.share_button_sr", { form: formName })}
          onClick={(e) => {
            e.stopPropagation();
            onShare(script, scriptForm);
          }}
        >
          <Share2 className="h-3 w-3" />
          <span className="sr-only">{t("dashboard.table.share_button_sr", { form: formName })}</span>
        </Button>
      </div>
    </div>
  );
});

FormScoreCell.displayName = "FormScoreCell";
